import React from "react";
import { Box, ButtonBase, Paper, Typography } from "@mui/material";
import { motion, useReducedMotion } from "framer-motion";
import { useNavigate } from "react-router-dom";

import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import AssignmentIndRoundedIcon from "@mui/icons-material/AssignmentIndRounded";
import FactCheckRoundedIcon from "@mui/icons-material/FactCheckRounded";
import LibraryBooksRoundedIcon from "@mui/icons-material/LibraryBooksRounded";
import LockRoundedIcon from "@mui/icons-material/LockRounded";
import PlagiarismIcon from "@mui/icons-material/Plagiarism";
import SchoolRoundedIcon from "@mui/icons-material/SchoolRounded";

import AccountBalanceRoundedIcon from "@mui/icons-material/AccountBalanceRounded";
import { obterTransicaoPaginaInicial } from "../../componentes/comuns/TransicaoPagina";

import {
  textosGlobais,
  iconesGlobais,
  espacamentosGlobais,
  tamanhosControlesGlobais,
  tamanhosPaginasAdministrativas,
} from "../../estilos/global";

// ======================================================
// ATALHOS DO ANALISTA
// ======================================================

const ATALHOS = [
  {
    titulo: "Processos",
    descricao: "Consulte e analise processos.",
    rota: "/analise",
    Icone: PlagiarismIcon,
  },
  {
    titulo: "Cadastros",
    descricao: "Analise cadastros pendentes.",
    rota: "/analise_cadastro",
    Icone: FactCheckRoundedIcon,
  },
  {
    titulo: "Escolas",
    descricao: "Acesse informações das escolas.",
    rota: "/escolas-geral",
    Icone: SchoolRoundedIcon,
  },
  {
    titulo: "Mantenedoras",
    descricao: "Acesse informações das mantenedoras.",
    rota: "/mantenedora",
    Icone: AccountBalanceRoundedIcon,
  },
  {
    titulo: "Modelos e Roteiros",
    descricao: "Consulte modelos e roteiros.",
    rota: "/modelos",
    Icone: LibraryBooksRoundedIcon,
  },
];

// ======================================================
// ATIVIDADES — PROJEÇÃO FUTURA
// Sem dados, quantidades ou vínculos simulados
// ======================================================

const ATIVIDADES = [
  {
    titulo: "Cadastros pendentes",
    descricao: "Escolas e mantenedoras aguardando análise.",
    Icone: FactCheckRoundedIcon,
    itens: [
      {
        texto: "Escolas pendentes",
        Icone: SchoolRoundedIcon,
      },
      {
        texto: "Mantenedoras pendentes",
        Icone: AccountBalanceRoundedIcon,
      },
    ],
  },
  {
    titulo: "Processos pendentes",
    descricao: "Processos aguardando análise.",
    Icone: PlagiarismIcon,
    itens: [
      { texto: "Documentos devolvidos" },
      { texto: "Processos aguardando análise" },
      { texto: "Acompanhamento de processos" },
    ],
  },
  {
    titulo: "Escolas atribuídas",
    descricao: "Escolas sob responsabilidade do Analista.",
    Icone: SchoolRoundedIcon,
    itens: [
      { texto: "Escolas vinculadas" },
      { texto: "Acompanhamento de escolas" },
      { texto: "Vínculos do Analista" },
    ],
  },
];

// ======================================================
// ESTILOS REUTILIZÁVEIS
// ======================================================

const CAIXA_ICONE = {
  width: 44,
  height: 44,
  display: "grid",
  placeItems: "center",
  flexShrink: 0,
  borderRadius: 1.5,
  color: "#087cf0",
  backgroundColor: "#e8f4ff",
};

const TITULO_CARD = {
  ...textosGlobais.tituloSecao,
  fontSize: tamanhosPaginasAdministrativas.tituloSecao,
  color: "#0767d8",
  overflowWrap: "anywhere",
};

const DESCRICAO_CARD = {
  ...textosGlobais.textoPadrao,
  fontSize: tamanhosControlesGlobais.descricao,
  color: "#6b7f99",
};

// ======================================================
// CARTÃO DE ATIVIDADE
// ======================================================

function CartaoAtividade({ titulo, descricao, Icone, itens }) {
  return (
    <Paper
      elevation={0}
      sx={{
        minWidth: 0,
        p: {
          xs: 2,
          lg: 1.25,
        },
        borderRadius: 1.5,
        border: "1px solid #dcecff",
        display: "flex",
        flexDirection: "column",
        gap: {
          xs: 1.5,
          lg: 1,
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.25,
        }}
      >
        <Box sx={CAIXA_ICONE}>
          <Icone
            sx={{
              fontSize: iconesGlobais.secao.fontSize,
            }}
          />
        </Box>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={TITULO_CARD}>{titulo}</Typography>

          <Typography sx={DESCRICAO_CARD}>{descricao}</Typography>
        </Box>

        <Typography
          sx={{
            ...TITULO_CARD,
            px: 1.5,
            bgcolor: "#e8f4ff",
            borderRadius: 5,
          }}
        >
          —
        </Typography>
      </Box>

      {/* ITENS ILUSTRATIVOS */}
      <Box
        sx={{
          display: "grid",
          gap: {
            xs: 1,
            lg: 0.5,
          },
          mt: {
            xs: 0.5,
            lg: 0,
          },
        }}
      >
        {itens.map(({ texto, Icone: IconeItem }) => (
          <Box
            key={texto}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.1,
              py: {
                xs: 0.8,
                lg: 0.35,
              },
              px: 1,
              borderRadius: 1,
              backgroundColor: "#f0f7ff",
            }}
          >
            {IconeItem ? (
              <IconeItem
                sx={{
                  color: "#087cf0",
                  fontSize: iconesGlobais.secao.fontSize,
                }}
              />
            ) : (
              <Box
                sx={{
                  width: 7,
                  height: 7,
                  borderRadius: "50%",
                  bgcolor: "#087cf0",
                }}
              />
            )}

            <Typography
              sx={{
                ...DESCRICAO_CARD,
                flex: 1,
              }}
            >
              {texto}
            </Typography>

            <Typography sx={DESCRICAO_CARD}>—</Typography>
          </Box>
        ))}
      </Box>
    </Paper>
  );
}

// ======================================================
// PAINEL INICIAL DO ANALISTA
// ======================================================

export default function PainelAnalista() {
  const navigate = useNavigate();
  const reduzirMovimento = useReducedMotion();

  return (
    <Box
      sx={{
        width: "100%",
        minWidth: 0,
        py: 0,
      }}
    >
      {/* =====================================================
          CABEÇALHO
      ===================================================== */}

      <Box
        component={motion.div}
        {...obterTransicaoPaginaInicial("cabecalho", 0, reduzirMovimento)}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.4,
          mb: {
            xs: espacamentosGlobais.secoes,
            lg: 1.5,
          },
        }}
      >
        <Box sx={CAIXA_ICONE}>
          <AssignmentIndRoundedIcon sx={{ fontSize: 27 }} />
        </Box>

        <Box sx={{ minWidth: 0 }}>
          <Typography
            component="h1"
            sx={{
              ...textosGlobais.tituloPagina,
              fontSize: tamanhosPaginasAdministrativas.tituloPagina,
              color: "#0767d8",
            }}
          >
            Painel do Analista
          </Typography>

          <Typography
            sx={{
              ...textosGlobais.textoPadrao,
              fontSize: tamanhosControlesGlobais.descricao,
              mt: espacamentosGlobais.tituloDescricao,
              color: "#7b8798",
            }}
          >
            Acompanhe rapidamente os principais módulos e pendências do seu
            fluxo de análise.
          </Typography>
        </Box>
      </Box>

      {/* =====================================================
          CARTÕES DE NAVEGAÇÃO
      ===================================================== */}

      <Box
        component="nav"
        aria-label="Módulos do Analista"
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "repeat(2, minmax(0, 1fr))",
            md: "repeat(3, minmax(0, 1fr))",
            lg: "repeat(5, minmax(0, 1fr))",
          },
          gap: 1.25,
          mb: {
            xs: espacamentosGlobais.secoes,
            lg: 1.5,
          },
        }}
      >
        {ATALHOS.map(({ titulo, descricao, rota, Icone }, indice) => (
          <Paper
            key={rota}
            component={motion.div}
            {...obterTransicaoPaginaInicial("card", indice, reduzirMovimento)}
            elevation={0}
            sx={{
              minWidth: 0,
              border: "1px solid #c9def4",
              borderRadius: 2,
              overflow: "hidden",
              boxShadow: "0 2px 6px rgba(25,70,120,0.08)",
              "&:hover": {
                boxShadow: "0 4px 12px rgba(25,70,120,0.14)",
              },
            }}
          >
            <ButtonBase
              onClick={() => navigate(rota)}
              aria-label={`Acessar ${titulo}`}
              sx={{
                width: "100%",
                minHeight: {
                  xs: 132,
                  lg: 112,
                },
                p: 1.5,
                display: "flex",
                alignItems: "flex-start",
                gap: 1.2,
                textAlign: "left",
                position: "relative",
              }}
            >
              <Box sx={CAIXA_ICONE}>
                <Icone
                  sx={{
                    fontSize: iconesGlobais.secao.fontSize,
                  }}
                />
              </Box>

              <Box
                sx={{
                  minWidth: 0,
                  flex: 1,
                  pb: 3,
                }}
              >
                <Typography component="h2" sx={TITULO_CARD}>
                  {titulo}
                </Typography>

                <Typography
                  sx={{
                    ...DESCRICAO_CARD,
                    mt: espacamentosGlobais.tituloDescricao,
                  }}
                >
                  {descricao}
                </Typography>
              </Box>

              {/* SETA DE ACESSO */}
              <Box
                sx={{
                  position: "absolute",
                  right: 12,
                  bottom: 12,
                  width: 28,
                  height: 28,
                  display: "grid",
                  placeItems: "center",
                  borderRadius: "50%",
                  backgroundColor: "#eaf4ff",
                  color: "#087cf0",
                }}
              >
                <ArrowForwardRoundedIcon sx={{ fontSize: 18 }} />
              </Box>
            </ButtonBase>
          </Paper>
        ))}
      </Box>

      {/* =====================================================
          ATIVIDADES — EM DESENVOLVIMENTO
      ===================================================== */}

      <Paper
        component={motion.section}
        {...obterTransicaoPaginaInicial(
          "card",
          ATALHOS.length,
          reduzirMovimento,
        )}
        aria-labelledby="titulo-atividades-analista"
        aria-describedby="status-atividades-analista"
        elevation={0}
        sx={{
          p: {
            xs: 2,
            lg: 1.5,
          },
          border: "1px solid #dcecff",
          borderRadius: 2,
          backgroundColor: "#e8f4ff",
        }}
      >
        {/* TÍTULO DA SEÇÃO */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.4,
            mb: espacamentosGlobais.descricaoCampos,
          }}
        >
          <Typography
            id="titulo-atividades-analista"
            component="h2"
            sx={TITULO_CARD}
          >
            Atividades
          </Typography>

          <Box
            sx={{
              flex: 1,
              height: 1,
              bgcolor: "#a8d0ff",
            }}
          />
        </Box>

        <Box
          sx={{
            position: "relative",
            overflow: "hidden",
            borderRadius: 1.5,
          }}
        >
          {/* CONTEÚDO FUTURO ILUSTRATIVO */}
          <Box
            aria-hidden="true"
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                md: "repeat(3, minmax(0, 1fr))",
              },
              gap: espacamentosGlobais.secoes,
              opacity: 0.45,
              filter: "blur(1px)",
              pointerEvents: "none",
              userSelect: "none",
            }}
          >
            {ATIVIDADES.map((atividade) => (
              <CartaoAtividade key={atividade.titulo} {...atividade} />
            ))}
          </Box>

          {/* BLOQUEIO CENTRAL */}
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              p: 2,
              backgroundColor: "rgba(255,255,255,0.68)",
            }}
          >
            <Box
              id="status-atividades-analista"
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.4,
                maxWidth: "100%",
                p: 2,
                border: "1px solid #c9def4",
                borderRadius: 2,
                backgroundColor: "#ffffff",
                boxShadow: "0 4px 14px rgba(8,124,240,0.08)",
              }}
            >
              <LockRoundedIcon
                sx={{
                  color: "#087cf0",
                  fontSize: iconesGlobais.secao.fontSize,
                  flexShrink: 0,
                }}
              />

              <Box sx={{ minWidth: 0 }}>
                <Typography component="h3" sx={TITULO_CARD}>
                  Em desenvolvimento
                </Typography>

                <Typography
                  sx={{
                    ...DESCRICAO_CARD,
                    mt: espacamentosGlobais.tituloDescricao,
                  }}
                >
                  Os indicadores serão disponibilizados em uma próxima
                  atualização.
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
}
